import React, { useState, useEffect, useRef } from 'react';
import logo from 'images/filmik-horizontal-logo-black.svg'; 
import Footer from 'components/shared/Footer';

interface VerifyCodeProps {
  method: 'sms' | 'email';
  contactInfo?: string;
  onSuccess: () => void;
  onResend?: () => void;
  onChangeMethod?: () => void;
  createdAt?: string; // ISO string for when the code was created
}

export const VerifyCode: React.FC<VerifyCodeProps> = ({
  method,
  contactInfo,
  onSuccess,
  onResend,
  onChangeMethod,
  createdAt
}) => {
  const [otpValues, setOtpValues] = useState<string[]>(Array(6).fill(''));
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [timeLeft, setTimeLeft] = useState(180); // 3 minutes
  const [isExpired, setIsExpired] = useState(false);
  
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Countdown timer effect
  useEffect(() => {
    if (!createdAt) return;

    try {
      const createdTime = new Date(createdAt).getTime() - 2;
      let interval = null;
      const updateTimer = () => {
        const now = new Date().getTime() ;
        const elapsed = Math.floor((now - createdTime) / 1000);
        const remaining = Math.max(0, 182 - elapsed);
        console.log(now, createdAt, createdTime, elapsed);
        setTimeLeft(remaining);
        
        if (remaining === 0) {
          setIsExpired(true);
          if(interval!=null) clearInterval(interval);
        } else {
          setIsExpired(false);
        }
      };

      // Initial update
      updateTimer();

      // Set up interval
      interval = setInterval(updateTimer, 1000);

      return () => clearInterval(interval);
    } catch (error) {
      console.error('Error setting up timer:', error);
      // If there's an error parsing the date, assume it's expired
      setIsExpired(true);
      setTimeLeft(0);
    }
  }, [createdAt]);

  // Format time display
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Mask contact info
  const getMaskedContact = () => {
    if (!contactInfo) return '';
    
    if (method === 'sms') {
      // Mask phone number: show only last 4 digits
      if (contactInfo.length <= 4) return contactInfo;
      return '*'.repeat(contactInfo.length - 4) + contactInfo.slice(-4);
    } else {
      // Mask email: show first and last char of local part
      const [local, domain] = contactInfo.split('@');
      if (!domain) return contactInfo; // Invalid email format
      if (local.length <= 2) return contactInfo;
      const maskedLocal = `${local[0]}${'*'.repeat(local.length - 2)}${local[local.length - 1]}`;
      return `${maskedLocal}@${domain}`;
    }
  };

  // Handle input change
  const handleInputChange = (index: number, value: string) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1);
    
    const newValues = [...otpValues];
    newValues[index] = digit;
    setOtpValues(newValues);
    setError(''); // Clear error when user types

    // Auto-focus next input
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all fields are filled
    if (newValues.every(val => val !== '') && newValues.join('').length === 6) {
      setTimeout(() => {
        handleSubmit(newValues.join(''));
      }, 100);
    }
  };

  // Handle backspace
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && otpValues[index] === '' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text');
    const digits = paste.replace(/\D/g, '').split('').slice(0, 6);

    const newValues = Array(6).fill('');
    digits.forEach((digit, i) => {
      if (i < 6) newValues[i] = digit;
    });

    setOtpValues(newValues);

    // Focus appropriate input
    const nextEmpty = digits.length < 6 ? digits.length : 5;
    inputRefs.current[nextEmpty]?.focus();

    // Auto-submit if complete
    if (digits.length === 6) {
      setTimeout(() => {
        handleSubmit(digits.join(''));
      }, 100);
    }
  };

  // Submit verification
  const handleSubmit = async (codeOverride?: string) => {
    const code = codeOverride || otpValues.join('');
    if (code.length < 6) return;

    setIsLoading(true);
    setError('');
    
    try {
      const response = await fetch('/two_factor_verification/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ code })
      });

      const data = await response.json();
      
      if (response.ok) {
        if (data.redirect_url) {
          window.location.href = data.redirect_url;
        } else {
          onSuccess();
        }
      } else {
        setError(data.error || 'Verification failed');
        // Clear the OTP inputs on error
        setOtpValues(Array(6).fill(''));
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error. Please try again.');
      // Clear the OTP inputs on error
      setOtpValues(Array(6).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  // Resend code
  const handleResend = async () => {
    setIsResending(true);
    setError('');
    setSuccessMessage('');
    
    try {
      if(!isExpired) {
        return;
      }
      console.log(`Attempting to resend ${method} code...`);
      
      const response = await fetch('/two_factor_verification/resend', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin',
        body: JSON.stringify({ method: method }) // Include method in request
      });

      console.log(`Resend response status: ${response.status}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Resend response data:', data);
        
        setSuccessMessage(`Code sent successfully via ${method.toUpperCase()}!`);
        setIsExpired(false);
        setTimeLeft(180);
        
        // Update created time if provided
        if (data.created_at) {
          // This should trigger the parent to update
          onResend?.();
        }
        
        // Clear OTP inputs
        setOtpValues(Array(6).fill(''));
        inputRefs.current[0]?.focus();
        
        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        const data = await response.json();
        console.error('Resend failed:', data);
        setError(data.error || 'Failed to resend code');
      }
    } catch (error) {
      console.error('Network error during resend:', error);
      setError('Network error. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <div className="flex-grow flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white rounded-lg p-8 shadow-md text-center">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img src={logo} alt="Filmik Logo" className="h-8"/>
          </div>

          <h2 className="text-2xl font-semibold mb-2 text-gray-800">Verification Code</h2>
          <p className="text-gray-600 mb-6">For your security, we need to verify your identity</p>

          <p className="text-gray-700 mb-6">
            We've sent a 6-digit code to<br />
            <span className="font-medium">{getMaskedContact()}</span>
          </p>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
              {successMessage}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
              {error}
            </div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            <div className="mb-4">
              {/* OTP Input Boxes */}
              <div className="flex justify-center space-x-2 mb-4">
                {Array(6).fill(0).map((_, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={2}
                    value={otpValues[index] ? '*' : ''}
                    onChange={(e) => handleInputChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    className="w-12 h-12 text-center text-lg font-semibold border border-gray-300 rounded-md focus:border-blue-500 focus:ring-blue-500 focus:outline-none"
                    disabled={isLoading}
                    required
                  />
                ))}
              </div>

              {/* Countdown */}
              <p className="text-sm text-gray-500 text-center mb-6">
                {isExpired ? (
                  <span className="text-red-500">Code has expired</span>
                ) : (
                  <>Code expires in <span>{formatTime(timeLeft)}</span></>
                )}
              </p>
            </div>

            {/* Verify Button */}
            <div className="mb-6">
              <button
                type="submit"
                disabled={isLoading || otpValues.join('').length < 6 || isExpired}
                className="w-full bg-blue-500 text-white font-bold py-2 px-4 rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Verifying...' : 'Verify Code'}
              </button>
            </div>
          </form>

          {/* Action Links */}
          <div className="text-sm text-gray-600 space-y-2 mb-6">
            <button
              onClick={handleResend}
              disabled={isResending}
              className="block w-full hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isResending ? 'Resending...' : timeLeft > 60 ? `Resend Code (${formatTime(timeLeft)})` : 'Resend Code'}
            </button>
            
            {/* {onChangeMethod && (
              <button
                onClick={onChangeMethod}
                className="block w-full hover:underline"
                disabled={isLoading}
              >
                Change Authentication Method
              </button>
            )} */}
          </div>
        </div>
      </div>
      {/* <Footer className="w-full" /> */}
    </div>
  );
};